import { CATEGORIES } from './types'
import type { CalcFn, Category, IndicatorDef } from './types'

/**
 * Every indicator version, discovered from the file system: `defs/<id>/v<N>.meta.ts`
 * (eager — small metadata the picker, parser and legends need) paired by path
 * with `defs/<id>/v<N>.calc.ts` (lazy — one chunk per version, loaded the first
 * time an instance is created). Adding an indicator is adding a folder.
 */

const metas = import.meta.glob<IndicatorDef>('./defs/*/v*.meta.ts', { eager: true, import: 'default' })
const calcs = import.meta.glob<CalcFn>('./defs/*/v*.calc.ts', { import: 'default' })

export const defKey = (d: Pick<IndicatorDef, 'id' | 'version'>): string => `${d.id}@${d.version}`

const byKey = new Map<string, IndicatorDef>()
const byId = new Map<string, IndicatorDef[]>()
const loaders = new Map<string, () => Promise<CalcFn>>()
/** meta path → def, so the invariants test can check that paths agree with ids/versions. */
export const metaPaths = new Map<string, IndicatorDef>()

for (const [path, def] of Object.entries(metas)) {
  metaPaths.set(path, def)
  byKey.set(defKey(def), def)
  const list = byId.get(def.id) ?? []
  list.push(def)
  byId.set(def.id, list)
  const loader = calcs[path.replace(/\.meta\.ts$/, '.calc.ts')]
  if (loader) loaders.set(defKey(def), loader)
}
for (const list of byId.values()) list.sort((a, b) => a.version - b.version)

/** Calc files with no meta beside them (an invariant the registry test asserts is empty). */
export const orphanCalcPaths = Object.keys(calcs).filter((p) => !metas[p.replace(/\.calc\.ts$/, '.meta.ts')])

export function allDefs(): IndicatorDef[] {
  return [...byKey.values()]
}

/** A saved entry's def; `version` omitted ⇒ a pre-library entry ⇒ version 1. */
export function resolveDef(id: string, version = 1): IndicatorDef | undefined {
  return byKey.get(`${id}@${version}`)
}

export function versionsOf(id: string): IndicatorDef[] {
  return byId.get(id) ?? []
}

/** The one addable version of an id (none for dropped / builtin-only ids). */
export function latestListed(id: string): IndicatorDef | undefined {
  return versionsOf(id).find((d) => d.listed)
}

const categoryRank = new Map<Category, number>(CATEGORIES.map((c, i) => [c.id, i]))
const rankOf = (d: IndicatorDef): number => (d.category && categoryRank.get(d.category)) ?? CATEGORIES.length

/** The picker's list: by category (CATEGORIES order), then `order`, then label. */
export function listListed(): IndicatorDef[] {
  return allDefs()
    .filter((d) => d.listed)
    .sort((a, b) => {
      const ra = rankOf(a)
      const rb = rankOf(b)
      if (ra !== rb) return ra - rb
      const ao = a.order ?? 1000
      const bo = b.order ?? 1000
      if (ao !== bo) return ao - bo
      return a.label.localeCompare(b.label)
    })
}

export interface ListedCategory {
  category: Category
  label: string
  defs: IndicatorDef[]
}

/** The listed defs grouped by category, in CATEGORIES order; empty categories are omitted. */
export function listListedByCategory(): ListedCategory[] {
  const listed = listListed()
  return CATEGORIES.map((c) => ({ category: c.id, label: c.label, defs: listed.filter((d) => d.category === c.id) })).filter(
    (g) => g.defs.length > 0,
  )
}

export function calcLoaderFor(def: IndicatorDef): (() => Promise<CalcFn>) | undefined {
  return loaders.get(defKey(def))
}
